/* eslint-disable @typescript-eslint/no-explicit-any */
// "Messages" — the patient's conversations with their care team. URS_12.1 (ACDPA-223).
//
// ⛔ THIS TALKS TO THE KERNEL, NOT BLAZE. blaze has no thread, conversation or message model —
// its `send-message` socket event is registered with no handler and its QuickBlox chat routes
// are all commented out. A message sent anywhere but the kernel is a message the portal inbox
// never shows, which is the whole acceptance criterion.
//
// ⭐ THE LIST IS SUMMARIES. The kernel deliberately does not send message bodies on this route;
// the detail screen fetches the turns. Do not "optimise" by asking for bodies here — it would
// put a patient's whole correspondence on the wire to paint a list that shows none of it.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  IonContent,
  IonPage,
  IonList,
  IonItem,
  IonLabel,
  IonNote,
  IonRefresher,
  IonRefresherContent,
  IonSkeletonText,
  IonToast,
  IonFab,
  IonFabButton,
  IonIcon,
  IonModal,
  IonTextarea,
  IonInput,
  IonButton,
  RefresherEventDetail,
  useIonRouter,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from "@ionic/react";
import { App } from "@capacitor/app";
import { PluginListenerHandle } from "@capacitor/core";
import { addOutline, chatbubbleEllipsesOutline, closeOutline } from "ionicons/icons";
import { motion } from "framer-motion";
import Header from "../../components/Header";
import NewMessageIndicator from "../../components/engagement/NewMessageIndicator";
import { useHaptics } from "../../hooks/useHaptics";
import { useEngagementFeatures } from "../../hooks/useEngagementFeatures";
import {
  MyThreadSummary,
  engagementErrorMessage,
  listMyThreads,
  startThread,
} from "../../services/engagement";
import "./Engagement.css";

// F-234 — second half of F-195: the list (message counts + unread badges) only ever loaded once
// on mount, plus pull-to-refresh, so a portal reply left it stale until the patient left the
// screen and came back. Same mechanism as the thread's poll (`CareTeamThread.tsx`): a
// `window.setInterval` gated on `isActiveRef`, kept current by the Ionic view lifecycle
// (`useIonViewWillEnter`/`useIonViewDidLeave` — this screen is cached, not unmounted, on
// navigation) and by an `@capacitor/app` foreground listener for a reply that arrived while the
// phone was locked.
const MESSAGES_LIST_POLL_MS = 15000;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
};

const itemVariants = {
  hidden: { y: 16, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring" as const, stiffness: 100, damping: 15 },
  },
};

/** Short, local, and never a raw ISO string on screen. */
export const formatWhen = (iso: string): string => {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  const today = new Date();
  const sameDay = when.toDateString() === today.toDateString();
  return sameDay
    ? when.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : when.toLocaleDateString([], { month: "short", day: "numeric" });
};

const CareTeamMessages: React.FC = () => {
  const router = useIonRouter();
  const { buttonTap, successActivity, errorFeedback } = useHaptics();
  const { features, isLoading: featuresLoading, hasOrganization } = useEngagementFeatures();

  const [threads, setThreads] = useState<MyThreadSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Guards against overlapping requests: the mount load, the poll tick, the pull-to-refresh and
  // ionViewWillEnter can all fire close together, and a slow response must not stack a second
  // one behind it. Mirrors `CareTeamThread.tsx`.
  const isLoadingRef = useRef(false);
  // Whether this screen is actually the one on screen right now. Starts true because the first
  // render IS the patient opening the list; useIonViewDidLeave/WillEnter (fired by the Ionic
  // router when this cached view is navigated away from and back to) keep it current after
  // that — Ionic caches this screen in the view stack rather than unmounting it on navigation,
  // so a plain mount/unmount effect could not tell "left" from "backgrounded".
  const isActiveRef = useRef(true);
  const isMountedRef = useRef(true);

  const load = useCallback(async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      const data = await listMyThreads();
      if (!isMountedRef.current) return;
      setThreads(data.items);
    } catch (err) {
      if (isMountedRef.current) {
        setError(engagementErrorMessage(err, "Your messages could not be loaded."));
      }
    } finally {
      if (isMountedRef.current) setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Poll every ~15s while mounted; a tick that lands while the screen is backgrounded (or
  // another load is already in flight) is simply skipped rather than queued.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (isActiveRef.current) void load();
    }, MESSAGES_LIST_POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  // These fire on every enter/leave of the cached view, including the first one, so
  // ionViewWillEnter doubles as an immediate refresh on return rather than waiting for the next
  // poll tick.
  useIonViewWillEnter(() => {
    isActiveRef.current = true;
    void load();
  });
  useIonViewDidLeave(() => {
    isActiveRef.current = false;
  });

  // Refresh immediately when the OS brings the app back to the foreground while this screen is
  // the one on screen — otherwise a reply sent while the phone was locked waits for the next
  // poll tick.
  useEffect(() => {
    let handle: PluginListenerHandle | undefined;
    let cancelled = false;

    void App.addListener("appStateChange", (state) => {
      if (state.isActive && isActiveRef.current) {
        void load();
      }
    }).then((added) => {
      if (cancelled) {
        void added.remove();
      } else {
        handle = added;
      }
    });

    return () => {
      cancelled = true;
      void handle?.remove();
    };
  }, [load]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await load();
    event.detail.complete();
  };

  const send = async () => {
    if (!body.trim() || isSending) return;
    setIsSending(true);
    try {
      const thread = await startThread(body, subject.trim() || null);
      await successActivity();
      setComposeOpen(false);
      setSubject("");
      setBody("");
      router.push(`/messages/${thread.id}`, "forward");
    } catch (err) {
      // ⛔ SURFACED, NEVER SWALLOWED. A refused send (MESSAGING_DISABLED) means the care team
      // switched messaging off after this screen loaded; the patient must see why their
      // question did not go, with their text still in the box.
      await errorFeedback();
      setError(engagementErrorMessage(err, "Your message could not be sent."));
    } finally {
      setIsSending(false);
    }
  };

  const showSkeleton = isLoading || featuresLoading;

  return (
    <IonPage>
      {/* F-481 (Leah, UAT2) — this back-button detail screen showed the Home greeting ("Welcome,")
          and the stacked-avatar icon it never asked for: `Header` defaults `showGreeting` and
          `showUserIcon` to true and this screen simply never overrode them, unlike every other
          back-button screen (`MyProfile.tsx`, `MyDevices.tsx`, …). Turning both off removes the
          word "Welcome" and the now-pointless greeting row, which is also what closes up the
          empty band of space that sat above "Messages". */}
      <div className="engagement-messages-header">
        <Header
          showGreeting={false}
          showUserIcon={false}
          showPageTitle
          pageTitle="Messages"
          showBackButton
          onBackButtonClick={() => router.goBack()}
        />
      </div>
      <IonContent className="engagement-content">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {showSkeleton ? (
          <IonList>
            {[0, 1, 2].map((i) => (
              <IonItem key={i}>
                <IonLabel>
                  <IonSkeletonText animated style={{ width: "60%" }} />
                  <IonSkeletonText animated style={{ width: "35%" }} />
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        ) : !hasOrganization ? (
          <div className="engagement-empty">
            <IonIcon icon={chatbubbleEllipsesOutline} />
            <h2>No care team yet</h2>
            <p>
              Messaging becomes available once a provider is looking after your therapy. You can
              connect one from Settings &rsaquo; Data Sharing.
            </p>
          </div>
        ) : threads.length === 0 ? (
          <div className="engagement-empty">
            <IonIcon icon={chatbubbleEllipsesOutline} />
            <h2>No messages yet</h2>
            <p>
              {features.messaging_enabled
                ? `Ask ${features.org_name ?? "your care team"} a question at any time.`
                : `${features.org_name ?? "Your care team"} has turned messaging off for now.`}
            </p>
          </div>
        ) : (
          <motion.div variants={containerVariants} initial="hidden" animate="visible">
            <IonList>
              {threads.map((thread) => (
                <motion.div key={thread.id} variants={itemVariants}>
                  <IonItem
                    button
                    detail
                    onClick={() => {
                      void buttonTap();
                      router.push(`/messages/${thread.id}`, "forward");
                    }}
                  >
                    <IonLabel>
                      <h2
                        className={
                          thread.unread_from_care_team > 0 ? "engagement-thread-unread" : undefined
                        }
                      >
                        {thread.subject || "Message to my care team"}
                      </h2>
                      <IonNote>
                        {thread.message_count} message{thread.message_count === 1 ? "" : "s"}
                      </IonNote>
                    </IonLabel>
                    {/* ⭐ E12d/R1 — THE UNREAD INDICATOR ON THE THREAD. `unread_from_care_team`
                        is the kernel's count of staff turns this patient has not opened; it
                        falls to 0 when the thread screen posts its read receipt. ⛔ Never
                        derive this in the app: this route carries no message bodies. */}
                    {/* F-482 (UAT3): green "New", never a red count — see NewMessageIndicator. */}
                    {thread.unread_from_care_team > 0 && (
                      <NewMessageIndicator count={thread.unread_from_care_team} slot="end" />
                    )}
                    <IonNote slot="end">{formatWhen(thread.last_activity_at)}</IonNote>
                  </IonItem>
                </motion.div>
              ))}
            </IonList>
          </motion.div>
        )}

        {/* ⚠️ The compose button is HIDDEN, not disabled, when messaging is off — URS_12.5 AC 2
            says the app mirrors the portal's configuration, and a greyed button invites a tap
            that can only ever be refused. History above stays visible either way (AC 3). */}
        {features.messaging_enabled && (
          <IonFab slot="fixed" vertical="bottom" horizontal="end" className="engagement-fab">
            <IonFabButton
              aria-label="Message my care team"
              onClick={() => {
                void buttonTap();
                setComposeOpen(true);
              }}
            >
              <IonIcon icon={addOutline} />
            </IonFabButton>
          </IonFab>
        )}

        {/* F-481 (Leah, UAT2) — `--background` set inline the way `OtpPopup.tsx` does it: the
            sheet inherited the page's `--ion-background-color` (the teal-gradient brand var), so
            the compose popup read as bright blue. White here only; the Send button below keeps
            its own `--ion-color-primary` blue untouched. */}
        <IonModal
          isOpen={composeOpen}
          onDidDismiss={() => setComposeOpen(false)}
          style={{ "--background": "#ffffff" } as React.CSSProperties}
        >
          <div className="engagement-sheet">
            <div className="engagement-sheet-head">
              <h2>Message my care team</h2>
              <IonButton fill="clear" onClick={() => setComposeOpen(false)} aria-label="Close">
                <IonIcon icon={closeOutline} slot="icon-only" />
              </IonButton>
            </div>
            {/* F-481 (Leah, UAT2) — URS_12.1: shown every time the patient opens this window,
                verbatim. Static in the markup (not a toast that fires once) so it is on screen
                for as long as the compose sheet is. */}
            <p className="engagement-compose-disclaimer">
              This inbox is not monitored regularly. For urgent care questions, call your clinic
              directly. If you are experiencing a medical emergency, call 911 immediately.
            </p>
            <IonInput
              className="engagement-compose-field"
              fill="outline"
              label="Subject (optional)"
              labelPlacement="stacked"
              value={subject}
              maxlength={200}
              onIonInput={(e) => setSubject(String(e.detail.value ?? ""))}
            />
            <IonTextarea
              className="engagement-compose-field"
              fill="outline"
              label="Your question"
              labelPlacement="stacked"
              autoGrow
              rows={5}
              value={body}
              onIonInput={(e) => setBody(String(e.detail.value ?? ""))}
            />
            <IonButton expand="block" disabled={!body.trim() || isSending} onClick={send}>
              {isSending ? "Sending…" : "Send"}
            </IonButton>
          </div>
        </IonModal>

        <IonToast
          isOpen={!!error}
          message={error ?? ""}
          duration={5000}
          color="danger"
          onDidDismiss={() => setError(null)}
        />
      </IonContent>
    </IonPage>
  );
};

export default CareTeamMessages;
